import gsap from 'gsap';
import { resolveColor } from './color.js';
import { createGlow } from './glow.js';
import { createSparkles } from './sparkles.js';
import { createRing } from './ring.js';

// ─── Config ───────────────────────────────────────────────────────────────────

const effects = {
  glow: createGlow,
  sparkles: createSparkles,
  ring: createRing,
};

/** How lively the effect is in each state: 0 is still, 1 is full divination. */

const energyByState = { idle: 0.35, loading: 1, result: 0.2 };

const energyDuration = 1.2;
const maxPixelRatio = 2;
const maxDelta = 0.05;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// ─── Helpers ──────────────────────────────────────────────────────────────────

function createEffect(name) {
  const factory = effects[name];

  if (!factory) {
    throw new Error(
      `magic-ball: unknown effect "${name}", expected one of ${Object.keys(effects).join(', ')}`
    );
  }

  return factory();
}

function readSettings(root) {
  const style = getComputedStyle(root);

  const number = (name, fallback) => {
    const value = parseFloat(style.getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  };

  const palette = (name) => {
    const colors = style
      .getPropertyValue(name)
      .split(',')
      .filter((color) => color.trim())
      .map((color) => resolveColor(color));

    return colors.length ? colors : [[255, 255, 255]];
  };

  return {
    speed: number('--magic-ball-speed', 1),
    radius: number('--magic-ball-radius', 0.38),
    count: number('--magic-ball-particle-count', 3000),
    sparsity: number('--magic-ball-sparsity', 0.55),
    intensity: number('--magic-ball-intensity', 1),
    accent: resolveColor(style.getPropertyValue('--magic-ball-accent')),
    glow: resolveColor(style.getPropertyValue('--magic-ball-glow-color')),
    spark: resolveColor(style.getPropertyValue('--magic-ball-spark-color')),
    palette: palette('--magic-ball-palette'),
    askPalette: palette('--magic-ball-ask-palette'),
  };
}

// ─── Layer ────────────────────────────────────────────────────────────────────

/**
 * Owns the canvas: sizing, the render loop and the energy tween. Effects only
 * draw a frame. Reads its settings from the block's custom properties and
 * follows `data-magic-ball` / inline style changes, so the sandbox can retune
 * it without talking to this module.
 */

export function createEffectLayer({ root, canvas }) {
  const controller = new AbortController();
  const context = canvas.getContext('2d');

  const frame = {
    width: 0,
    height: 0,
    radius: 0,
    time: 0,
    delta: 0,
    energy: energyByState[root.dataset.state] ?? energyByState.idle,
    still: reducedMotion.matches,
    settings: readSettings(root),
  };

  let effect = createEffect(root.dataset.magicBall);
  let visible = true;
  let running = false;

  function draw() {
    if (!frame.width) return;

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.globalCompositeOperation = 'source-over';
    context.clearRect(0, 0, frame.width, frame.height);
    context.globalCompositeOperation = 'lighter';
    context.globalAlpha = 1;
    effect.render(context, frame);
  }

  function tick(time, deltaTime) {
    frame.delta = Math.min(deltaTime / 1000, maxDelta) * frame.settings.speed;
    frame.time += frame.delta * (0.5 + frame.energy);
    draw();
  }

  function syncLoop() {
    const shouldRun = visible && !frame.still;

    if (shouldRun === running) return;

    running = shouldRun;

    if (running) {
      gsap.ticker.add(tick);
      return;
    }

    gsap.ticker.remove(tick);
    frame.delta = 0;
    draw();
  }

  function resize() {
    const ratio = Math.min(window.devicePixelRatio || 1, maxPixelRatio);

    frame.width = Math.round(canvas.clientWidth * ratio);
    frame.height = Math.round(canvas.clientHeight * ratio);
    frame.radius = frame.width * frame.settings.radius;
    canvas.width = frame.width;
    canvas.height = frame.height;
    effect.resize(frame);
    draw();
  }

  function setEffect(name) {
    effect.destroy();
    effect = createEffect(name);
    effect.resize(frame);
    effect.setState(root.dataset.state, frame);
    draw();
  }

  /**
   * Returns the effect's promise for a loading run it plays to the end (the
   * ring), or null when the effect has no end of its own and the caller keeps time.
   */

  function setState(state) {
    const energy = energyByState[state] ?? energyByState.idle;
    const finished = effect.setState(state, frame) ?? null;

    if (frame.still) {
      frame.energy = energy;
      draw();
      return finished;
    }

    gsap.to(frame, {
      energy,
      duration: energyDuration,
      ease: 'sine.inOut',
      overwrite: true,
    });

    return finished;
  }

  /** Hover only eases a running effect; a still frame ignores it. */

  function setHover(on) {
    if (frame.still) return;

    effect.setHover?.(on);
  }

  /** Tinting eases a running effect too; a still frame ignores it. */

  function setTint(on) {
    if (frame.still) return;

    effect.setTint?.(on);
  }

  function handleMutation(records) {
    if (records.some(({ attributeName }) => attributeName === 'data-magic-ball')) {
      setEffect(root.dataset.magicBall);
    }

    frame.settings = readSettings(root);
    resize();
  }

  function handleMotionChange(event) {
    gsap.killTweensOf(frame);
    frame.still = event.matches;
    frame.energy = energyByState[root.dataset.state] ?? energyByState.idle;
    syncLoop();
  }

  const resizeObserver = new ResizeObserver(resize);
  const mutationObserver = new MutationObserver(handleMutation);
  const intersectionObserver = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    syncLoop();
  });

  resizeObserver.observe(root);
  mutationObserver.observe(root, { attributeFilter: ['data-magic-ball', 'style'] });
  intersectionObserver.observe(canvas);
  reducedMotion.addEventListener('change', handleMotionChange, { signal: controller.signal });

  effect.setState(root.dataset.state, frame);
  syncLoop();

  return {
    setState,
    setHover,
    setTint,

    destroy() {
      controller.abort();
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      intersectionObserver.disconnect();
      gsap.ticker.remove(tick);
      gsap.killTweensOf(frame);
      effect.destroy();
    },
  };
}
